// AUPRÉA — Catalogue consultant (amélioration post-étape 8)
// IMPORTANT : aucun prix n'est modifié. Affichage uniquement.
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { C, fmtEur, Btn, Input, Spinner, Alert, prestationsApi } from "./shared";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// ── Groupes et sous-sections (règles figées côté UI) ─────────────────────────
const PARTICULIERS_GROUPS = [
  {
    titre: "Anticiper",
    intro: "Pour organiser sa succession de son vivant.",
    codes: ["CONS", "AUDIT", "ATELIER", "DUO", "SUIVI"],
  },
  {
    titre: "Après un décès",
    intro: "Pour accompagner une famille qui doit ouvrir une succession.",
    codes: ["FDR", "POSTDC"],
  },
  {
    titre: "Foncier Clair · Outre-mer",
    intro: "Pour un bien occupé sans titre ou issu d'une indivision.",
    codes: ["FCF", "FCA"],
  },
  {
    titre: "Carnet digital · abonnements",
    intro: "Pour centraliser documents, volontés et personnes de confiance.",
    codes: ["ESS", "CONF", "PPLUS"],
  },
];
const PARTICULIERS_ORIENTATIONS = ["ORI_NOT", "ORI_CGP"];

const PROFESSIONNELS_GROUPS = [
  {
    titre: "Prestations à l'unité",
    intro: "",
    codes: ["CONSD", "PCD", "PCDM", "AGF", "COORD", "SUIVD"],
  },
  {
    titre: "Packs",
    intro: "Plusieurs prestations réunies, à prix réduit.",
    codes: ["PACKC", "PACKS", "PACKT"],
  },
  {
    titre: "En cas de décès du dirigeant",
    intro: "",
    codes: ["POSTDD"],
  },
];
const PROFESSIONNELS_ORIENTATIONS = ["ORI_ASS", "ORI_NOT"];

// Mentions spécifiques (hors prix, pour infos visuelles)
const EXTRA_NOTES = {
  FDR:    "Les 89 € de la feuille de route sont déduits du forfait post-décès si celui-ci est pris.",
  POSTDC: "Les 89 € de la feuille de route sont déduits si vous prenez aussi la feuille de route.",
  FCF:    "Les 69 € de la fiche sont déduits de l'accompagnement Foncier Clair.",
  FCA:    "Les 69 € de la fiche Foncier Clair sont déduits si vous prenez aussi la fiche.",
};

const PACK_COMPARAISON = {
  PACKC: { au_lieu_de: "au lieu de 1 180 € HT" },
  PACKS: { au_lieu_de: "valeur 1 990 € HT", recommande: true },
  PACKT: { au_lieu_de: "au lieu de 2 780 € HT" },
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const isAbo = (p) => p.type === "abonnement_mensuel";
const isOrientation = (p) => p.type === "orientation_gratuite";
const isApartirDe = (p) => (p.prix_affiche || "").toLowerCase().includes("à partir de") || (p.prix_affiche || "").toLowerCase().includes("a partir de");
const hasRemise = (code) => ["FDR", "POSTDC", "FCF", "FCA"].includes(code);

const fmtEurPrecise = fmtEur;

const pricePieces = (p, tvaPct) => {
  // Retourne {ht, ttc, unite, baseLabel, affiche}
  if (isOrientation(p)) return { ht: 0, ttc: 0, unite: "", baseLabel: "Gratuit", affiche: "Gratuit" };
  const prix = Number(p.prix || 0);
  if (p.base_prix === "HT") {
    const ttc = Math.round(prix * (1 + tvaPct / 100) * 100) / 100;
    return { ht: prix, ttc, unite: p.unite || "", baseLabel: "HT", affiche: p.prix_affiche || `${fmtEur(prix)} HT` };
  }
  // TTC ou NA
  return { ht: null, ttc: prix, unite: p.unite || "", baseLabel: "TTC", affiche: p.prix_affiche || `${fmtEur(prix)}` };
};

// ── Composants ────────────────────────────────────────────────────────────────
const SubHeader = ({ titre, intro }) => (
  <div style={{ marginBottom: 12, marginTop: 6 }}>
    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 500, color: C.navy }}>{titre}</div>
    {intro && <div style={{ fontSize: 12, color: C.mid, marginTop: 4 }}>{intro}</div>}
  </div>
);

const CommissionLine = ({ prestation, palierInfo, discret }) => {
  if (discret) return null;
  if (!palierInfo) return null;
  if (isOrientation(prestation)) return null;
  const taux = palierInfo.taux_actuel;
  const palier = palierInfo.palier_actuel;
  const tauxAbo = palierInfo.taux_abonnement;
  const tvaPct = palierInfo.tva_pct || 8.5;
  const pr = pricePieces(prestation, tvaPct);
  const montantTTC = pr.ttc;

  // Admin : 3 taux au lieu d'un montant
  if (palierInfo.role === "admin") {
    if (isAbo(prestation)) {
      const c20 = Math.round(montantTTC * tauxAbo * 100) / 100;
      return (
        <div style={commissionBoxStyle} data-testid="commission-admin">
          <div style={commissionLabelStyle}>Commission consultant (abonnement {Math.round(tauxAbo*100)}%)</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.golddm }}>{fmtEurPrecise(c20)} /mois</div>
        </div>
      );
    }
    const s = Math.round(montantTTC * palierInfo.taux_starter * 100) / 100;
    const c = Math.round(montantTTC * palierInfo.taux_confirme * 100) / 100;
    const e = Math.round(montantTTC * palierInfo.taux_expert * 100) / 100;
    const ap = isApartirDe(prestation) ? "à partir de " : "";
    return (
      <div style={commissionBoxStyle} data-testid="commission-admin">
        <div style={commissionLabelStyle}>Commission consultant</div>
        <div style={{ fontSize: 11, color: C.mid, display: "flex", flexWrap: "wrap", gap: 10, marginTop: 2 }}>
          <span>Starter&nbsp;<strong style={{ color: C.navy }}>{ap}{fmtEurPrecise(s)}</strong></span>
          <span>Confirmé&nbsp;<strong style={{ color: C.gold }}>{ap}{fmtEurPrecise(c)}</strong></span>
          <span>Expert&nbsp;<strong style={{ color: C.teal }}>{ap}{fmtEurPrecise(e)}</strong></span>
        </div>
      </div>
    );
  }

  // Consultant / partenaire_recruteur
  if (isAbo(prestation)) {
    const comm = Math.round(montantTTC * tauxAbo * 100) / 100;
    return (
      <div style={commissionBoxStyle} data-testid={`commission-${prestation.code}`}>
        <div style={commissionLabelStyle}>Votre commission</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.golddm }}>{fmtEurPrecise(comm)} <span style={{ fontSize: 11, fontWeight: 500 }}>/mois</span></div>
        <div style={{ fontSize: 10, color: C.mid, marginTop: 2 }}>Abonnement · {Math.round(tauxAbo*100)}% tant que le client reste abonné</div>
      </div>
    );
  }

  const comm = Math.round(montantTTC * taux * 100) / 100;
  const prefix = isApartirDe(prestation) ? "à partir de " : "";
  return (
    <div style={commissionBoxStyle} data-testid={`commission-${prestation.code}`}>
      <div style={commissionLabelStyle}>Votre commission</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.golddm }}>{prefix}{fmtEurPrecise(comm)}</div>
      <div style={{ fontSize: 10, color: C.mid, marginTop: 2 }}>
        Palier {palier} · {Math.round(taux*100)}%
        {hasRemise(prestation.code) && <span style={{ color: C.amber }}> · hors remise</span>}
      </div>
    </div>
  );
};

const Card = ({ prestation: p, palierInfo, discret, tvaPct }) => {
  const pr = pricePieces(p, tvaPct);
  const packInfo = PACK_COMPARAISON[p.code];
  return (
    <div data-testid={`card-${p.code}`} style={{
      background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12,
      padding: 14, display: "flex", flexDirection: "column", position: "relative",
      ...(packInfo?.recommande ? { borderColor: C.gold, borderWidth: 1.5, boxShadow: `0 4px 16px ${C.gold}22` } : {}),
    }}>
      {packInfo?.recommande && (
        <div style={{ position: "absolute", top: -10, right: 10, background: C.gold, color: C.navy, fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 999, textTransform: "uppercase", letterSpacing: ".06em" }} data-testid="recommande-badge">
          ◆ Recommandé
        </div>
      )}
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, fontFamily: "monospace", letterSpacing: ".04em", marginBottom: 2 }}>{p.code}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 4, lineHeight: 1.3 }}>{p.nom}</div>
      <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.5, marginBottom: 8 }}>{p.description}</div>
      {p.quand_proposer && (
        <div style={{ display: "flex", gap: 6, fontSize: 11, color: C.teal, lineHeight: 1.4, marginBottom: 10, background: C.teallt + "66", borderRadius: 6, padding: "6px 8px" }}>
          <span style={{ flexShrink: 0 }}>💡</span>
          <span><strong style={{ color: C.tealdm }}>Quand la proposer :</strong> {p.quand_proposer}</span>
        </div>
      )}
      {EXTRA_NOTES[p.code] && (
        <div style={{ fontSize: 10, color: C.amber, marginBottom: 8, fontStyle: "italic" }}>↳ {EXTRA_NOTES[p.code]}</div>
      )}

      {/* Tarif */}
      <div style={{ marginTop: "auto", padding: "10px 12px", background: `linear-gradient(135deg, ${C.goldlt} 0%, #fff 100%)`, borderRadius: 8, borderLeft: `3px solid ${C.gold}`, marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: C.mid, textTransform: "uppercase", letterSpacing: ".05em" }}>Tarif</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: C.navy }} data-testid={`price-${p.code}`}>{pr.affiche}</div>
          {pr.baseLabel !== "Gratuit" && (
            <span style={{ fontSize: 10, fontWeight: 700, color: pr.baseLabel === "HT" ? C.amber : C.teal, background: (pr.baseLabel === "HT" ? C.amberlt : C.teallt), padding: "1px 6px", borderRadius: 4 }}>{pr.baseLabel}</span>
          )}
        </div>
        {pr.baseLabel === "HT" && pr.ttc > 0 && (
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }} data-testid={`price-ttc-${p.code}`}>soit {fmtEurPrecise(pr.ttc)} TTC{pr.unite || ""}</div>
        )}
        {packInfo?.au_lieu_de && (
          <div style={{ fontSize: 10, color: C.golddm, marginTop: 2, fontWeight: 600 }}>↓ {packInfo.au_lieu_de}</div>
        )}
      </div>

      <CommissionLine prestation={p} palierInfo={palierInfo} discret={discret} />
    </div>
  );
};

const OrientationBox = ({ prestations, orientationsCodes }) => {
  const items = orientationsCodes.map(code => prestations.find(p => p.code === code)).filter(Boolean);
  if (!items.length) return null;
  return (
    <div style={{ marginTop: 20, padding: 16, background: C.navylt + "66", borderRadius: 12, border: `.5px solid ${C.border}` }} data-testid="orientations-box">
      <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 }}>Orientations sans frais</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
        {items.map(p => (
          <div key={p.id} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.navy, marginBottom: 4 }}>{p.nom}</div>
            <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.4, marginBottom: 6 }}>{p.description}</div>
            {p.quand_proposer && <div style={{ fontSize: 10, color: C.tealdm, fontStyle: "italic" }}>💡 {p.quand_proposer}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════
export const CatalogueConseiller = ({ onDemarrerParcours }) => {
  const [prestations, setPrestations] = useState([]);
  const [palierInfo, setPalierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => localStorage.getItem("auprea_cat_tab") || "particuliers");
  const [discret, setDiscret] = useState(() => localStorage.getItem("auprea_cat_discret") === "1");
  const [q, setQ] = useState("");

  useEffect(() => { localStorage.setItem("auprea_cat_tab", tab); }, [tab]);
  useEffect(() => { localStorage.setItem("auprea_cat_discret", discret ? "1" : "0"); }, [discret]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      prestationsApi.list(),
      axios.get(`${API}/consultant/palier-info`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` },
      }).then(r => r.data),
    ]).then(([pr, pi]) => {
      setPrestations(pr.filter(p => p.actif));
      setPalierInfo(pi);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const tvaPct = palierInfo?.tva_pct || 8.5;
  const byCode = useMemo(() => {
    const m = {};
    prestations.forEach(p => { m[p.code] = p; });
    return m;
  }, [prestations]);

  const matchesQ = (p) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return p.nom.toLowerCase().includes(needle) || p.description.toLowerCase().includes(needle);
  };

  const groups = tab === "particuliers" ? PARTICULIERS_GROUPS : PROFESSIONNELS_GROUPS;
  const orientationsCodes = tab === "particuliers" ? PARTICULIERS_ORIENTATIONS : PROFESSIONNELS_ORIENTATIONS;
  const launchType = tab === "particuliers" ? "bilan_succession" : "bilan_dirigeants";
  const launchLabel = tab === "particuliers" ? "Bilan Succession" : "Bilan Dirigeants";

  if (loading) return <Spinner />;

  // Bandeau palier
  const showBandeau = palierInfo && palierInfo.role !== "admin" && !discret;
  const bandeauTxt = showBandeau ? (() => {
    const pct = Math.round(palierInfo.taux_actuel * 100);
    const nb = palierInfo.nb_ventes_mois;
    const encore = palierInfo.encore_avant_prochain;
    const pr = palierInfo.prochain_palier;
    const trPr = palierInfo.taux_prochain ? Math.round(palierInfo.taux_prochain * 100) : null;
    const ventesTxt = `${nb} vente${nb > 1 ? "s" : ""}`;
    if (pr) {
      return `Votre palier ce mois-ci : ${palierInfo.palier_actuel} (${pct}%) · ${ventesTxt} · encore ${encore} vente${encore > 1 ? "s" : ""} pour passer ${pr} (${trPr}%)`;
    }
    return `Votre palier ce mois-ci : ${palierInfo.palier_actuel} (${pct}%) · ${ventesTxt} · palier maximum atteint 🏆`;
  })() : null;

  return (
    <div>
      {/* Bandeau palier */}
      {bandeauTxt && (
        <div data-testid="palier-bandeau" style={{
          background: `linear-gradient(135deg, ${C.goldlt} 0%, #fff 100%)`,
          border: `1px solid ${C.gold}66`, borderRadius: 10, padding: "10px 14px",
          marginBottom: 16, fontSize: 13, color: C.navy, fontWeight: 500,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>🏅</span>
          <span>{bandeauTxt}</span>
        </div>
      )}

      {/* Barre de contrôles : tabs + mode discret */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "inline-flex", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
          {[
            { k: "particuliers", lbl: "Particuliers" },
            { k: "professionnels", lbl: "Professionnels" },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`} style={{
              background: tab === t.k ? C.navy : "transparent",
              color: tab === t.k ? "#fff" : C.mid,
              border: "none", padding: "7px 18px", borderRadius: 8, cursor: "pointer",
              fontSize: 13, fontWeight: 600, fontFamily: "inherit", transition: "all .15s",
            }}>{t.lbl}</button>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
          <Input placeholder="🔍 Rechercher par nom..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input" />
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: C.mid, background: discret ? C.navylt : "#fff", border: `1px solid ${discret ? C.navy : C.border}`, borderRadius: 10, padding: "7px 12px" }} data-testid="discret-toggle">
          <input type="checkbox" checked={discret} onChange={(e) => setDiscret(e.target.checked)} style={{ accentColor: C.navy, cursor: "pointer" }} />
          <span>👁 Mode discret {discret && <strong style={{ color: C.navy }}>(commissions masquées)</strong>}</span>
        </label>
        <Btn variant="gold" onClick={() => onDemarrerParcours?.(launchType)} data-testid="btn-lancer-parcours">
          ▶ Lancer un {launchLabel}
        </Btn>
      </div>

      {discret && (
        <Alert type="info">
          <span>👁</span>
          <span>Mode discret activé : toutes les lignes de commission et le bandeau palier sont masqués. Vous pouvez montrer cet écran à votre client en toute confiance.</span>
        </Alert>
      )}

      {/* Groupes */}
      {groups.map(g => {
        const items = g.codes.map(code => byCode[code]).filter(Boolean).filter(matchesQ);
        if (!items.length) return null;
        return (
          <div key={g.titre} data-testid={`group-${g.titre}`} style={{ marginBottom: 28 }}>
            <SubHeader titre={g.titre} intro={g.intro} />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
              {items.map(p => (
                <Card key={p.id} prestation={p} palierInfo={palierInfo} discret={discret} tvaPct={tvaPct} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Orientations */}
      <OrientationBox prestations={prestations} orientationsCodes={orientationsCodes} />
    </div>
  );
};

// ── Styles ──────────────────────────────────────────────────────────────────
const commissionBoxStyle = {
  padding: "8px 10px",
  background: "#F7F3EA",
  borderRadius: 6,
  borderLeft: `3px solid ${C.golddm}`,
};
const commissionLabelStyle = {
  fontSize: 9, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".06em",
};
